import { api } from "@/lib/api";
import { colonyApi } from "@/lib/colonyApi";
import { fileToCompressedDataUrl } from "@/lib/imageCompress";
import { toast } from "sonner";
import { registerPhotoTimes } from "@/lib/photoTimes";
import { saveOfflineMotion, getOfflineMotion, deleteOfflineMotion, OFFLINE_VIDEO_PREFIX } from "@/lib/offlineLiveMedia";

const isNetErr = (e) => !e?.response || e?.code === "ERR_NETWORK" || e?.code === "ECONNABORTED";

export const LIVE_PHOTO_PREFIX = "live-photo:";
const VIDEO_EXTENSIONS = new Set(["mov", "mp4", "m4v", "webm"]);
const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "heic", "heif", "webp", "gif"]);
const SERVER_PREVIEW_EXTENSIONS = new Set(["heic", "heif", "avif"]);
const CHUNK_BYTES = 720 * 1024;
const MOTION_SCAN_BYTES = 1024 * 1024;
const LARGE_STILL_BYTES = 6 * 1024 * 1024;

export function parseMediaValue(value) {
  if (typeof value !== "string" || !value.startsWith(LIVE_PHOTO_PREFIX)) {
    return { kind: "photo", image: value || "", live: false };
  }
  try {
    const parsed = JSON.parse(value.slice(LIVE_PHOTO_PREFIX.length));
    if (!parsed?.image || !parsed?.video_id) throw new Error("bad live photo");
    return { kind: "live_photo", live: true, ...parsed };
  } catch {
    return { kind: "photo", image: "", live: false, invalid: true };
  }
}

export const mediaStill = (value) => parseMediaValue(value).image || "";
export const isLivePhoto = (value) => parseMediaValue(value).live;
export const isImageMedia = (value) => mediaStill(value).startsWith("data:image/");
export const isPdfMedia = (value) => typeof value === "string" && value.startsWith("data:application/pdf");
export const makeLivePhotoValue = ({ image, videoId, contentType, filename }) => `${LIVE_PHOTO_PREFIX}${JSON.stringify({ image, video_id: videoId, video_type: contentType, filename: filename || "live-photo" })}`;

const extension = (file) => String(file?.name || "").split(".").pop().toLowerCase();
const baseName = (file) => String(file?.name || "").replace(/\.[^.]+$/, "").toLowerCase();
export const isImageFile = (file) => file?.type?.startsWith("image/") || IMAGE_EXTENSIONS.has(extension(file));
export const isVideoFile = (file) => file?.type?.startsWith("video/") || VIDEO_EXTENSIONS.has(extension(file));

// Apple Live Photo package (.livp is a ZIP of the original HEIC + MOV).
export const isMotionPackage = (file) => ["livp", "zip"].includes(extension(file)) || file?.type === "application/zip";

const indexOfBytes = (haystack, needle, from = 0) => {
  outer: for (let i = from; i <= haystack.length - needle.length; i++) {
    for (let j = 0; j < needle.length; j++) if (haystack[i + j] !== needle[j]) continue outer;
    return i;
  }
  return -1;
};

// Google/Samsung Motion Photos are a single JPEG with an MP4 appended. Detect
// the embedded video so we can preserve the ORIGINAL file instead of
// recompressing it (which would strip the motion).
export async function hasEmbeddedMotion(file) {
  try {
    if (!isImageFile(file)) return false;
    const header = new Uint8Array(await file.slice(0, 3).arrayBuffer());
    if (!(header[0] === 0xff && header[1] === 0xd8)) return false; // not a JPEG
    // Scan bounded slices instead of loading an entire 20–80 MB Android photo
    // into memory before it is resized. This avoids gallery-only tab crashes.
    for (let offset = 512; offset < file.size; offset += MOTION_SCAN_BYTES - 8) {
      const bytes = new Uint8Array(await file.slice(offset, Math.min(file.size, offset + MOTION_SCAN_BYTES)).arrayBuffer());
      if (indexOfBytes(bytes, [0x66, 0x74, 0x79, 0x70]) >= 0) return true;
    }
    return false;
  } catch {
    return false;
  }
}

// Upload an ORIGINAL motion-photo file untouched; the server preserves the
// originals and returns a Live-Photo-compatible token for display.
export async function ingestMotionPhoto(file, { client = activeMediaClient(), scope = activeMediaScope(), colonyId } = {}) {
  const form = new FormData();
  form.append("file", file, file.name || "motion-photo");
  form.append("scope", scope);
  if (colonyId) form.append("colony_id", colonyId);
  const { data } = await client.post("/motion-photos/ingest", form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120_000 });
  return data.token;
}

async function normalizeStillPhoto(file, { client = activeMediaClient() } = {}) {
  const form = new FormData();
  form.append("file", file, file.name || "photo");
  const { data } = await client.post("/photos/normalize-upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120_000,
  });
  if (!data?.data_url?.startsWith("data:image/jpeg;base64,")) throw new Error("The photo preview could not be created.");
  return data.data_url;
}

async function prepareStillPhoto(file, { client } = {}) {
  const needsServer = SERVER_PREVIEW_EXTENSIONS.has(extension(file)) || /image\/(heic|heif|avif)/i.test(file.type || "") || file.size > LARGE_STILL_BYTES;
  if (needsServer && (typeof navigator === "undefined" || navigator.onLine)) {
    try { return await normalizeStillPhoto(file, { client }); } catch (error) {
      if (SERVER_PREVIEW_EXTENSIONS.has(extension(file)) || /image\/(heic|heif|avif)/i.test(file.type || "")) throw error;
    }
  }
  const image = await fileToCompressedDataUrl(file);
  if (/^data:image\/(heic|heif|avif)/i.test(image)) {
    throw new Error("This HEIF photo needs an internet connection to make a browser-safe preview.");
  }
  return image;
}

export function activeMediaClient() {
  return window.location.pathname.startsWith("/colon") ? colonyApi : api;
}

export function activeMediaScope() {
  const path = window.location.pathname;
  return path.includes("managementusa") || path.startsWith("/colon") ? "mgmt_usa_nj" : "pool";
}

const bufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  }
  return btoa(binary);
};

export async function uploadLiveMotion(file, { client = activeMediaClient(), scope = activeMediaScope(), colonyId, sharedAcrossColonies = false, onProgress } = {}) {
  const contentType = file.type || (extension(file) === "mov" ? "video/quicktime" : "video/mp4");
  const { data: started } = await client.post("/live-media/start", {
    filename: file.name || `live-photo-${Date.now()}.mp4`,
    content_type: contentType,
    size: file.size,
    scope,
    colony_id: colonyId || null,
    shared_across_colonies: sharedAcrossColonies,
  });
  const uploadId = started.upload_id;
  try {
    let index = 0;
    for (let offset = 0; offset < file.size; offset += CHUNK_BYTES) {
      const chunk = file.slice(offset, Math.min(file.size, offset + CHUNK_BYTES));
      const data = bufferToBase64(await chunk.arrayBuffer());
      await client.post("/live-media/chunk", { upload_id: uploadId, index, data });
      index += 1;
      onProgress?.(Math.min(95, Math.round((Math.min(file.size, offset + CHUNK_BYTES) / file.size) * 95)));
    }
    const { data } = await client.post("/live-media/finish", { upload_id: uploadId });
    onProgress?.(100);
    return data;
  } catch (error) {
    client.delete(`/live-media/upload/${uploadId}`).catch(() => {});
    throw error;
  }
}

export async function processMediaFiles(fileList, { max = 12, existingCount = 0, onProgress, client, scope, colonyId, sharedAcrossColonies = false } = {}) {
  const files = Array.from(fileList || []);
  const room = Math.max(0, max - existingCount);
  const items = [];
  const stamps = [];
  const addItem = (file, value) => { items.push(value); stamps.push([file, value]); };
  let paired = 0;
  let ignoredVideos = 0;
  let motionFailures = 0;
  let offlineSaved = 0;
  const portal = (client || activeMediaClient()) === colonyApi ? "colony" : "main";
  const effScope = scope || activeMediaScope();

  // 1) Real motion photos (Apple .livp packages, or Google/Samsung single-file
  // motion photos) are uploaded UNTOUCHED so the originals are preserved.
  const rest = [];
  for (const file of files) {
    if (items.length >= room) break;
    const pkg = isMotionPackage(file);
    const serverPreviewOnly = SERVER_PREVIEW_EXTENSIONS.has(extension(file)) || /image\/(heic|heif|avif)/i.test(file.type || "");
    const embedded = pkg || serverPreviewOnly ? false : await hasEmbeddedMotion(file);
    if (pkg || embedded) {
      // Offline: keep the ORIGINAL bytes on this device and ingest later.
      // (.livp stills are HEIC which the browser can't preview, so only
      // single-file motion photos take this path.)
      const keepOffline = async () => {
        if (!embedded) return false;
        const image = await fileToCompressedDataUrl(file);
        const localId = await saveOfflineMotion({
          kind: "ingest", blob: file, filename: file.name || "motion-photo.jpg",
          contentType: file.type || "image/jpeg", scope: effScope,
          colonyId: colonyId || null, sharedAcrossColonies, portal,
        });
        addItem(file, makeLivePhotoValue({ image, videoId: `${OFFLINE_VIDEO_PREFIX}${localId}`, contentType: "video/mp4", filename: file.name }));
        paired += 1;
        offlineSaved += 1;
        return true;
      };
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        try { if (await keepOffline()) continue; } catch { /* fall through to upload attempt */ }
      }
      try {
        const token = await ingestMotionPhoto(file, { client, scope, colonyId });
        addItem(file, token);
        paired += 1;
      } catch (e) {
        let kept = false;
        if (isNetErr(e)) { try { kept = await keepOffline(); } catch { kept = false; } }
        if (!kept && isImageFile(file)) { addItem(file, await prepareStillPhoto(file, { client })); motionFailures += 1; }
      }
    } else {
      rest.push(file);
    }
  }

  // 2) Everything else: ordinary images, plus in-app LIVE captures that arrive
  // as a still + matching-basename motion clip.
  const images = rest.filter(isImageFile);
  const videos = rest.filter(isVideoFile);
  const videosByBase = new Map(videos.map((file) => [baseName(file), file]));
  const picked = images.slice(0, Math.max(0, room - items.length));
  for (const imageFile of picked) {
    const videoFile = videosByBase.get(baseName(imageFile));
    const image = await prepareStillPhoto(imageFile, { client });
    if (!videoFile) {
      addItem(imageFile, image);
      continue;
    }
    videosByBase.delete(baseName(imageFile));
    // Offline (or connection drop mid-upload): keep the motion clip on this
    // device and upload it automatically once the connection returns.
    const keepOffline = async () => {
      const localId = await saveOfflineMotion({
        kind: "motion", blob: videoFile,
        contentType: videoFile.type || "video/mp4",
        filename: videoFile.name || `live-${Date.now()}.mp4`,
        scope: effScope, colonyId: colonyId || null, sharedAcrossColonies, portal,
      });
      addItem(imageFile, makeLivePhotoValue({ image, videoId: `${OFFLINE_VIDEO_PREFIX}${localId}`, contentType: videoFile.type || "video/mp4", filename: imageFile.name }));
      paired += 1;
      offlineSaved += 1;
    };
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      try { await keepOffline(); } catch { addItem(imageFile, image); motionFailures += 1; }
      continue;
    }
    try {
      const uploaded = await uploadLiveMotion(videoFile, { client, scope, colonyId, sharedAcrossColonies, onProgress });
      addItem(imageFile, makeLivePhotoValue({ image, videoId: uploaded.id, contentType: uploaded.content_type, filename: imageFile.name }));
      paired += 1;
    } catch (e) {
      if (isNetErr(e)) {
        try { await keepOffline(); continue; } catch { /* fall through */ }
      }
      addItem(imageFile, image);
      motionFailures += 1;
    }
  }
  ignoredVideos += videosByBase.size;
  if (paired) toast.message("Open the photo to watch its motion.");
  if (offlineSaved) toast.message("No internet — the motion is saved on this device and will upload automatically when you're back online.");
  if (motionFailures) toast.message("Motion could not upload, so the still photo was kept.");
  registerPhotoTimes(stamps, client);
  return { items, paired, ignoredVideos, motionFailures, offlineSaved, capped: files.length > room };
}

export async function downloadOriginalMotionPhoto(motionPhotoId, component = "motion", { client = activeMediaClient() } = {}) {
  const response = await client.get(`/motion-photos/${motionPhotoId}/original/${component}`, { responseType: "blob", timeout: 120_000 });
  const url = URL.createObjectURL(response.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `original-${component}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 8000);
}

export async function fetchLiveMotion(value, { client = activeMediaClient() } = {}) {
  const media = parseMediaValue(value);
  if (!media.live) throw new Error("Not a Live Photo");
  // Clips captured offline play straight from this device's storage.
  if (String(media.video_id).startsWith(OFFLINE_VIDEO_PREFIX)) {
    const rec = await getOfflineMotion(String(media.video_id).slice(OFFLINE_VIDEO_PREFIX.length));
    if (!rec?.blob) throw new Error("This motion clip hasn't synced to this device yet.");
    let blob = rec.blob;
    if (rec.kind === "ingest") {
      // Single-file motion photo: the MP4 is appended after the JPEG.
      const bytes = new Uint8Array(await rec.blob.arrayBuffer());
      const ftyp = indexOfBytes(bytes, [0x66, 0x74, 0x79, 0x70], 512);
      if (ftyp > 4) blob = new Blob([bytes.slice(ftyp - 4)], { type: "video/mp4" });
    }
    return URL.createObjectURL(blob);
  }
  const response = await client.get(`/live-media/${media.video_id}`, { responseType: "blob", timeout: 90_000 });
  return URL.createObjectURL(response.data);
}

export async function deleteLiveMotion(value, { client = activeMediaClient() } = {}) {
  const media = parseMediaValue(value);
  if (!media.live) return;
  if (String(media.video_id).startsWith(OFFLINE_VIDEO_PREFIX)) {
    await deleteOfflineMotion(String(media.video_id).slice(OFFLINE_VIDEO_PREFIX.length)).catch(() => {});
    return;
  }
  await client.delete(`/live-media/${media.video_id}`);
}